import React, { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  ColumnFilterOption,
  ColumnSizing,
} from 'components/ReorderResizeTable/types';
import {
  COLUMNS,
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  SuggestedActionsTableData,
  BODY_CONTENT_GENERATE,
  SuggestedActionColumns,
} from './data';
import SuggestedActionsDrawer from '../SuggestedActionsDrawer';
import { SuggestedActionsDrawerProps } from '../SuggestedActionsDrawer/SuggestedActionsDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useDrawerNavigation from 'hooks/useDrawerNavigation';

import './SuggestedActionsTable.styles.scss';

type SuggestedActionsTableProps = {
  className?: string;
  isLoading?: boolean;
  sort?: string;
  totalPages?: number;
  totalItems?: number;
  data: SuggestedActionsTableData[];
  columns?: SuggestedActionColumns;
  columnFilterOptions?: Record<string, ColumnFilterOption>;
  columnSizing?: ColumnSizing;
  onColumnSizingChange?: (columnSizing: ColumnSizing) => void;
  onReorder?: (columns: SuggestedActionColumns) => void;
  onPendingActionsChange?: SuggestedActionsDrawerProps['onPendingActionsChange'];
};

const SuggestedActionsTable: FC<SuggestedActionsTableProps> = (props) => {
  const {
    className,
    isLoading,
    sort,
    totalPages = 1,
    totalItems,
    data,
    columns = COLUMNS,
    columnFilterOptions,
    columnSizing,
    onColumnSizingChange,
    onReorder,
    onPendingActionsChange,
  } = props;

  const [selectedSuggestedAction, setSelectedSuggestedAction] =
    useState<SuggestedActionsTableData>();

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>();

  const classes = classNames('suggested-actions-table', className);

  const openSuggestedAction = useCallback(
    (suggestedAction: SuggestedActionsTableData) => {
      setSelectedSuggestedAction(suggestedAction);
      drawer?.setOpen(true);
    },
    [drawer],
  );

  const { onPrev, onNext } = useDrawerNavigation({
    rows: data,
    selectedId: selectedSuggestedAction?._id,
    totalPages,
    isLoading,
    onNavigate: openSuggestedAction,
  });

  const tableContentTransformations = useMemo(
    () =>
      COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (data: SuggestedActionsTableData) =>
            BODY_CONTENT_GENERATE[col]({
              data,
            }),
        }),
        {},
      ),
    [],
  );

  return (
    <>
      <ReorderResizeTable
        className={classes}
        sort={sort}
        totalPages={totalPages}
        totalItems={totalItems}
        recordName="action"
        data={data}
        columns={columns}
        headingTranslations={HEADING_TRANSLATIONS}
        sortingValues={SORTING_VALUES}
        isDataLoading={isLoading}
        tableContentTransformations={tableContentTransformations}
        customSizes={CUSTOM_COL_SIZES}
        columnFilterOptions={columnFilterOptions}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onReorder={onReorder}
        onRowClick={openSuggestedAction}
      />

      <SuggestedActionsDrawer
        ref={drawerRef}
        suggestedAction={selectedSuggestedAction}
        onPendingActionsChange={onPendingActionsChange}
        onPrev={onPrev}
        onNext={onNext}
      />
    </>
  );
};

export default SuggestedActionsTable;
